/**
 * ChartTooltip — themed tooltip content shared by all chart wrappers.
 * Passed to recharts via `content={<ChartTooltip ... />}`; recharts injects
 * `active` / `payload` / `label` at render time. We type the injected props
 * loosely (not via recharts' internal TooltipProps, which churns across major
 * versions) so the wrappers stay version-resilient.
 */
import styles from './Charts.module.css';

export interface ChartTooltipEntry {
  name?: string | number;
  value?: number | string;
  color?: string;
  dataKey?: string | number;
  payload?: Record<string, unknown>;
}

export interface ChartTooltipProps {
  active?: boolean;
  payload?: ChartTooltipEntry[];
  label?: string | number;
  /** Format each entry's value (e.g. currency, %). Defaults to localized number. */
  valueFormatter?: (value: number | string | undefined, entry: ChartTooltipEntry) => string;
  /** Hide the label row (e.g. donuts where the slice name is the label). */
  hideLabel?: boolean;
}

const defaultFormat = (v: number | string | undefined): string =>
  typeof v === 'number' ? v.toLocaleString() : String(v ?? '');

export function ChartTooltip({
  active,
  payload,
  label,
  valueFormatter,
  hideLabel = false,
}: ChartTooltipProps) {
  if (!active || !payload || payload.length === 0) return null;
  const fmt = valueFormatter ?? ((v: number | string | undefined) => defaultFormat(v));
  return (
    <div className={styles.tooltip} role="presentation">
      {!hideLabel && label != null && label !== '' && (
        <div className={styles.tooltipLabel}>{label}</div>
      )}
      <ul className={styles.tooltipList}>
        {payload.map((entry, i) => (
          <li key={i} className={styles.tooltipRow}>
            {entry.color && (
              <span className={styles.tooltipSwatch} style={{ background: entry.color }} />
            )}
            {entry.name != null && entry.name !== '' && (
              <span className={styles.tooltipName}>{entry.name}</span>
            )}
            <span className={styles.tooltipValue}>{fmt(entry.value, entry)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
